import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Check, FileText, ShieldCheck, ChevronRight, Sparkles } from "lucide-react";
import SignDocumentModal from "@/components/SignDocumentModal";

export default function SignatureCentre({ application, signatureStatus, onSigned }) {
  const [docs, setDocs] = useState([]);
  const [openDoc, setOpenDoc] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/signature-pack");
      setDocs(data.documents || []);
    })();
  }, []);

  const signedMap = (application.signatures || []).reduce((acc, s) => {
    acc[s.document_id] = s;
    return acc;
  }, {});

  const allSigned = signatureStatus?.complete;
  const pct = signatureStatus ? Math.round((signatureStatus.signed_count / signatureStatus.total) * 100) : 0;

  return (
    <div className="bloom-card p-8 md:p-10" data-testid="signature-centre">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="text-[var(--bloom-brand)]" size={20} strokeWidth={1.5} />
            <h3 className="font-serif text-2xl">Signature Centre</h3>
          </div>
          <p className="font-serif italic text-[var(--bloom-ink-soft)] text-sm max-w-xl">
            Sign your Founding Grower Agreement and the supporting legal documents. Each signature
            is electronically witnessed with date, time, IP and device. Legally binding under the
            Contract and Commercial Law Act 2017.
          </p>
        </div>
        {allSigned ? (
          <div className="flex items-center gap-2 text-[var(--bloom-brand)] font-serif italic" data-testid="sig-complete-badge">
            <Sparkles size={16} /> All documents signed
          </div>
        ) : (
          <div className="text-right">
            <div className="bloom-label">Progress</div>
            <div className="font-serif text-2xl">
              {signatureStatus?.signed_count || 0}<span className="text-base text-[var(--bloom-ink-muted)]"> / {signatureStatus?.total || 7}</span>
            </div>
          </div>
        )}
      </div>

      <div className="bloom-progress-track mb-8">
        <div className="bloom-progress-fill" style={{ width: `${pct}%` }} />
      </div>

      <div className="space-y-3" data-testid="sig-doc-list">
        {docs.map((d) => {
          const signed = signedMap[d.id];
          return (
            <button
              key={d.id}
              onClick={() => !signed && setOpenDoc(d.id)}
              disabled={!!signed}
              className={`w-full text-left flex items-center justify-between gap-4 p-5 border transition ${
                signed
                  ? "border-[var(--bloom-brand)] bg-[var(--bloom-surface)] cursor-default"
                  : "border-[var(--bloom-border)] hover:bg-[var(--bloom-surface)] cursor-pointer"
              }`}
              data-testid={`sig-doc-${d.id}`}
            >
              <div className="flex items-start gap-4 min-w-0">
                <FileText className={signed ? "text-[var(--bloom-brand)]" : "text-[var(--bloom-ink-muted)]"} size={18} strokeWidth={1.5} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-serif text-lg leading-tight">{d.title}</span>
                    {d.is_primary && (
                      <span className="text-[9px] tracking-[0.15em] uppercase border border-[var(--bloom-brand)] text-[var(--bloom-brand)] px-2 py-0.5">Primary Agreement</span>
                    )}
                  </div>
                  <div className="text-xs text-[var(--bloom-ink-muted)] mt-1 truncate">{d.description}</div>
                  {signed && (
                    <div className="text-[11px] italic font-serif text-[var(--bloom-brand)] mt-2" data-testid={`sig-signed-${d.id}`}>
                      Signed as <em>{signed.signed_name}</em> · {new Date(signed.signed_at).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-shrink-0">
                {signed ? (
                  <span className="inline-flex items-center gap-1 text-[var(--bloom-brand)] text-sm font-serif"><Check size={14} /> Signed</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[var(--bloom-ink)] text-xs tracking-[0.15em] uppercase">Sign <ChevronRight size={12} /></span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {openDoc && (
        <SignDocumentModal
          documentId={openDoc}
          application={application}
          onClose={() => setOpenDoc(null)}
          onSigned={(data) => {
            setOpenDoc(null);
            onSigned?.(data);
          }}
        />
      )}
    </div>
  );
}
